/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { TabsItem } from 'types/tabsItem'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { removeSuccessStory, removeSuccessStoryVariables } from '__generatedTypes__/removeSuccessStory'
import { DELETE_SUCCESS_STORY } from 'queries/successStory'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { BlogPostList, BlogPostListCopy } from '../BlogPostList'
import { useBlogPostListSearchParams } from '../BlogPostList/hooks'
import { SuccessStoriesArticlesQueryParams, useSuccessStoriesArticlesQuery } from './hooks'
import { Props, SuccessStoryArticle } from './types'

type AllSuccessStoriesContentProps = Props & {
  status?: TabsItem
  showStatusTabs?: boolean
}

const SUCCESS_STORY_LIST_COPY: BlogPostListCopy = {
  deleteConfirmationSubtitle: 'Are you sure? This will permanently delete the success story from the database.',
  deleteErrorMessage: 'Failed to delete success story',
  deleteSuccessMessage: 'Success story deleted successfully',
  deleteTitle: 'Deleting Success Story',
  entityName: 'success story',
  titleColumn: 'Success Story Title',
}

export const AllSuccessStoriesContent = ({
  platform,
  status,
  showStatusTabs = true,
}: AllSuccessStoriesContentProps) => {
  const { getLink } = useLink()
  const { search, activeStatus: queryActiveStatus, page, take, updateTableParams } = useBlogPostListSearchParams()
  const activeStatus = status ?? queryActiveStatus
  const handleQueryParamsChange = useCallback(
    (params: SuccessStoriesArticlesQueryParams) => {
      const nextStatusId = params.filters.statusId ?? activeStatus.id

      if (
        params.page === page &&
        params.take === take &&
        params.search === search &&
        nextStatusId === activeStatus.id
      ) {
        return
      }

      updateTableParams({
        page: params.page,
        take: params.take,
        search: params.search,
        status: nextStatusId,
      })
    },
    [activeStatus.id, page, search, take, updateTableParams]
  )

  const articlesQuery = useSuccessStoriesArticlesQuery({
    page,
    take,
    search,
    platformId: platform.id,
    statusId: activeStatus.id,
    onChange: handleQueryParamsChange,
  })

  const [removeSuccessStory, removeSuccessStoryMutation] = useMutation<removeSuccessStory, removeSuccessStoryVariables>(
    DELETE_SUCCESS_STORY
  )

  const handleDelete = async (article: SuccessStoryArticle) => {
    await removeSuccessStory({
      variables: {
        removeSuccessStoryId: article.id,
      },
    })

    await articlesQuery.refetchCurrent()
  }

  return (
    <BlogPostList
      copy={SUCCESS_STORY_LIST_COPY}
      getEditLink={(article) => getLink(cmsRoutes.EDIT_SUCCESS_STORY, { successStoryId: article.id })}
      getSectionLink={(section) => getLink(cmsRoutes.ORGANIZE_EDIT_SUCCESS_STORIES, { id: section.id })}
      isDeleting={removeSuccessStoryMutation.loading || articlesQuery.loading}
      isLoading={!articlesQuery.loaded}
      items={articlesQuery.items}
      onDelete={handleDelete}
      showStatusTabs={showStatusTabs}
      status={status}
      totalCount={articlesQuery.count}
    />
  )
}

export const AllSuccessStories = withPlatformCheck(AllSuccessStoriesContent)
